import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  ViewChild,
  ViewEncapsulation,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { basicSetup } from 'codemirror';
import { EditorView, Decoration, DecorationSet, keymap, ViewUpdate } from '@codemirror/view';
import { Compartment, EditorState, StateField } from '@codemirror/state';
import { indentWithTab } from '@codemirror/commands';
import { javascript } from '@codemirror/lang-javascript';
import { linter, lintGutter, Diagnostic } from '@codemirror/lint';

function jsLintSource(view: EditorView): Diagnostic[] {
  const code = view.state.doc.toString();
  if (!code.trim()) {
    return [];
  }
  try {
    // eslint-disable-next-line no-new-func
    new Function(code);
    return [];
  } catch (e: unknown) {
    // Retry wrapped in parentheses: handles object literals that the parser
    // misreads as block statements (e.g. wrapperFirst="{").
    try {
      // eslint-disable-next-line no-new-func
      new Function('return (' + code + ')');
      return [];
    } catch { /* fall through to report the original error */ }

    const err = e as Error;
    // Try to parse line number from V8 stack message like "SyntaxError: ... at line N"
    // or "<anonymous>:N:M"
    let line = 1;
    let col = 0;
    const stackMatch = err.stack?.match(/<anonymous>:(\d+):(\d+)/);
    if (stackMatch) {
      // new Function wraps code in "function anonymous(...) {\n code \n}"
      // so the reported line is offset by 1
      line = Math.max(1, parseInt(stackMatch[1], 10) - 1);
      col = parseInt(stackMatch[2], 10);
    }
    const docLine = view.state.doc.line(Math.min(line, view.state.doc.lines));
    const from = docLine.from + Math.min(col, docLine.length);
    const to = Math.min(from + 1, docLine.to);
    return [{
      from,
      to,
      severity: 'error',
      message: err.message,
    }];
  }
}

const editorTheme = EditorView.theme({
  '&': {
    fontSize: '13px',
    fontFamily: '"JetBrains Mono", "Fira Mono", "Cascadia Code", monospace',
    border: '1px solid rgba(15,23,42,0.12)',
    borderRadius: '8px',
    background: '#f8fafc',
    outline: 'none',
  },
  '&.cm-focused': {
    borderColor: '#2563eb',
    boxShadow: '0 0 0 3px rgba(37,99,235,0.12)',
  },
  '.cm-scroller': {
    overflow: 'auto',
    minHeight: '120px',
    maxHeight: '320px',
  },
  '.cm-content': {
    padding: '8px 4px',
    caretColor: '#2563eb',
  },
  '.cm-gutters': {
    background: '#f1f5f9',
    borderRight: '1px solid rgba(15,23,42,0.08)',
    borderTopLeftRadius: '8px',
    borderBottomLeftRadius: '8px',
  },
  '.cm-lineNumbers .cm-gutterElement': {
    color: '#94a3b8',
    fontSize: '12px',
  },
  '.cm-activeLine': {
    background: 'rgba(37,99,235,0.04)',
  },
  '.cm-activeLineGutter': {
    background: 'rgba(37,99,235,0.06)',
  },
});

const protectedLineDeco = Decoration.line({ class: 'cm-protected-line' });

@Component({
  selector: 'app-js-editor',
  standalone: true,
  imports: [],
  encapsulation: ViewEncapsulation.None,
  template: `<div #host></div>`,
  styleUrls: ['./js-editor.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => JsEditorComponent),
    multi: true,
  }],
})
export class JsEditorComponent implements AfterViewInit, OnChanges, OnDestroy, ControlValueAccessor {
  @ViewChild('host') hostRef!: ElementRef<HTMLDivElement>;
  @Input() wrapperFirst = '';
  @Input() wrapperLast = '';

  private editor: EditorView | null = null;
  private pendingValue: string | null = null;
  private isUpdatingFromModel = false;
  private editableCompartment = new Compartment();

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  private get hasWrapper(): boolean {
    return !!this.wrapperFirst;
  }

  private wrapBody(body: string): string {
    return this.hasWrapper
      ? `${this.wrapperFirst}\n${body}\n${this.wrapperLast}`
      : body;
  }

  private extractBody(fullText: string): string {
    if (!this.hasWrapper) return fullText;
    const lines = fullText.split('\n');
    return lines.slice(1, -1).join('\n');
  }

  ngAfterViewInit(): void {
    const self = this;
    const hasWrapper = this.hasWrapper;

    const updateListener = EditorView.updateListener.of((update: ViewUpdate) => {
      if (update.docChanged && !self.isUpdatingFromModel) {
        const value = update.state.doc.toString();
        self.onChange(self.extractBody(value));
        self.onTouched();
      }
    });

    // Block edits to the first and last (wrapper) lines.
    // isUpdatingFromModel bypasses the filter for programmatic writeValue updates.
    const wrapperFilter = EditorState.transactionFilter.of(tr => {
      if (!tr.docChanged || !hasWrapper || self.isUpdatingFromModel) return tr;
      const doc = tr.startState.doc;
      if (doc.lines < 3) return tr;
      const firstLine = doc.line(1);
      const lastLine = doc.line(doc.lines);
      let blocked = false;
      tr.changes.iterChangedRanges((fromA, toA) => {
        // fromA <= firstLine.to: touches first line or its trailing newline
        // toA >= lastLine.from: touches the newline before last line or the last line itself
        if (fromA <= firstLine.to || toA >= lastLine.from) {
          blocked = true;
        }
      });
      return blocked ? [] : tr;
    });

    // Decorate the wrapper lines with a distinct visual style.
    const protectedLinesField = StateField.define<DecorationSet>({
      create(state) {
        if (!hasWrapper || state.doc.lines < 2) return Decoration.none;
        const decos = [protectedLineDeco.range(state.doc.line(1).from)];
        if (state.doc.lines >= 2) {
          decos.push(protectedLineDeco.range(state.doc.line(state.doc.lines).from));
        }
        return Decoration.set(decos);
      },
      update(decorations, tr) {
        if (!hasWrapper) return Decoration.none;
        if (!tr.docChanged) return decorations.map(tr.changes);
        const doc = tr.newDoc;
        if (doc.lines < 2) return Decoration.none;
        const decos = [protectedLineDeco.range(doc.line(1).from)];
        if (doc.lines >= 2) {
          decos.push(protectedLineDeco.range(doc.line(doc.lines).from));
        }
        return Decoration.set(decos);
      },
      provide: f => EditorView.decorations.from(f),
    });

    const initialDoc = this.wrapBody(this.pendingValue ?? '');

    this.editor = new EditorView({
      state: EditorState.create({
        doc: initialDoc,
        extensions: [
          basicSetup,
          keymap.of([indentWithTab]),
          javascript(),
          lintGutter(),
          linter(jsLintSource, { delay: 600 }),
          editorTheme,
          this.editableCompartment.of(EditorView.editable.of(true)),
          updateListener,
          wrapperFilter,
          protectedLinesField,
        ],
      }),
      parent: this.hostRef.nativeElement,
    });

    this.pendingValue = null;
  }

  ngOnChanges(): void {
    // Intentionally empty — value updates come via writeValue (ControlValueAccessor)
  }

  ngOnDestroy(): void {
    this.editor?.destroy();
    this.editor = null;
  }

  // ControlValueAccessor

  writeValue(value: string | null): void {
    const body = value ?? '';
    if (!this.editor) {
      this.pendingValue = body;
      return;
    }
    const display = this.wrapBody(body);
    const current = this.editor.state.doc.toString();
    if (current === display) {
      return;
    }
    this.isUpdatingFromModel = true;
    this.editor.dispatch({
      changes: { from: 0, to: this.editor.state.doc.length, insert: display },
    });
    this.isUpdatingFromModel = false;
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (!this.editor) {
      return;
    }
    this.editor.dispatch({
      effects: this.editableCompartment.reconfigure(EditorView.editable.of(!isDisabled)),
    });
  }
}
